import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath?: string;
  ogType?: string;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({ title, description, keywords, canonicalPath, ogType = 'website', schemaJson }: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    const baseUrl = 'http://911hl.cn/';
    const canonicalUrl = canonicalPath ? `${baseUrl}${canonicalPath}` : `${baseUrl}${location.pathname}`;

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);

    const schemaScripts: HTMLScriptElement[] = [];
    if (schemaJson) {
      const schemas = Array.isArray(schemaJson) ? schemaJson : [schemaJson];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        schemaScripts.push(script);
      });
    }

    return () => {
      schemaScripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalPath, ogType, location.pathname, schemaJson]);

  return null;
}